import React from 'react';
import { withTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { ProductService } from '../../services';
import { DefaultButton, LoadingButton } from '../buttons';
import { TextField, Checkbox } from '../fields';
import { Confirm, Toast } from '../alerts';
import { ModalLayout } from '../layouts';

import '../../styles/modals/modal.css';

const PRICE_ONLY = /^\+?\s*£?\s*(\d+(?:[.,]\d{1,2})?)$/;
const NAME_AND_PRICE = /^(.*?\S)\s+[-:]?\s*\+?\s*£?\s*(\d+(?:[.,]\d{1,2})?)$/;
const toPrice = (value) => Number(String(value).replace(',', '.'));

/** Parses "Olives +2.00" lines, or GloriaFood's name then price on the next line. Lines with no price are free. */
export const parseExtras = (text) => {
  const items = [];
  let pending = null;

  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const priceOnly = line.match(PRICE_ONLY);
      if (priceOnly && pending) {
        items.push({ name: pending, price: toPrice(priceOnly[1]) });
        pending = null;
        return;
      }
      const both = line.match(NAME_AND_PRICE);
      if (both) {
        if (pending) items.push({ name: pending, price: 0 });
        items.push({ name: both[1], price: toPrice(both[2]) });
        pending = null;
        return;
      }
      if (pending) items.push({ name: pending, price: 0 });
      pending = line;
    });

  if (pending) items.push({ name: pending, price: 0 });
  return items;
};

const emptyDraft = () => ({ name: '', items: [{ name: '', price: '' }], category_ids: [], product_ids: [], limit_on: false, max_selections: '', is_required: false });

/** Reusable extras lists (e.g. Pizza Toppings), applied to whole categories as secondary options. */
class ExtrasModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState();
    this.productService = new ProductService();
  }

  initialState() {
    return { show: false, venue: null, lists: [], categories: [], products: [], productSearch: '', activeId: null, draft: emptyDraft(), busy: false, error: null };
  }

  componentDidMount() {
    if (this.props.onRef) this.props.onRef(this);
  }

  componentWillUnmount() {
    if (this.props.onRef) this.props.onRef(undefined);
  }

  show(show, venue = null) {
    this.setState({ ...this.initialState(), show, venue }, () => {
      if (show && venue) this.load();
    });
  }

  errorFrom(e, fallback) {
    return (e && e.errors && e.errors[0]) || (e && e.message) || fallback;
  }

  async load(selectId = null) {
    const { venue } = this.state;
    this.setState({ busy: true, error: null });
    try {
      const result = await this.productService.getExtras(venue._id);
      if (!result || !result.success) throw result || new Error('Could not load extras');
      this.setState({ busy: false, lists: result.lists, categories: result.categories, products: result.products || [] }, () => {
        const target = result.lists.find((list) => list._id === selectId) || result.lists[0];
        if (target) this.select(target);
        else this.newList();
      });
    } catch (e) {
      this.setState({ busy: false, error: this.errorFrom(e, 'Could not load extras') });
    }
  }

  select(list) {
    this.setState({
      activeId: list._id,
      draft: {
        name: list.name,
        items: list.items.map((item) => ({ name: item.name, price: String(item.price) })),
        category_ids: list.category_ids.map(String),
        product_ids: (list.product_ids || []).map(String),
        limit_on: !!list.max_selections,
        max_selections: list.max_selections ? String(list.max_selections) : '',
        is_required: list.is_required === true,
      },
      error: null,
    });
  }

  newList() {
    this.setState({ activeId: null, draft: emptyDraft(), error: null });
  }

  updateDraft(patch) {
    this.setState({ draft: { ...this.state.draft, ...patch } });
  }

  setItem(index, patch) {
    this.updateDraft({ items: this.state.draft.items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  }

  removeItem(index) {
    const items = this.state.draft.items.filter((_, i) => i !== index);
    this.updateDraft({ items: items.length ? items : [{ name: '', price: '' }] });
  }

  onPasteList = (e) => {
    const text = e.clipboardData ? e.clipboardData.getData('text') : '';
    const parsed = parseExtras(text);
    if (!parsed.length) return;
    e.preventDefault();
    const existing = this.state.draft.items.filter((item) => item.name.trim());
    this.updateDraft({ items: [...existing, ...parsed.map((item) => ({ name: item.name, price: String(item.price) }))] });
    Toast.success(`${parsed.length} items added`);
  };

  toggleProduct(id, checked) {
    const ids = this.state.draft.product_ids.filter((x) => x !== id);
    this.updateDraft({ product_ids: checked ? [...ids, id] : ids });
  }

  toggleCategory(id, checked) {
    const ids = this.state.draft.category_ids.filter((x) => x !== id);
    this.updateDraft({ category_ids: checked ? [...ids, id] : ids });
  }

  save = async (apply = false) => {
    const { t } = this.props;
    const { venue, activeId, draft, categories } = this.state;

    if (apply) {
      const names = [
        ...categories.filter((c) => draft.category_ids.includes(c._id)).map((c) => c.name),
        ...this.state.products.filter((p) => draft.product_ids.includes(p._id)).map((p) => p.name),
      ].join(', ');
      const ok = await Confirm.open(
        t('Apply extras'),
        `${t('Apply')} "${draft.name}" ${t('to every product in')}: ${names}? ${t('Extras previously applied from this list are replaced. Extras added to individual products are kept.')}`,
        t('Yes'),
        t('No')
      );
      if (!ok) return;
    }

    if (draft.limit_on && !(Number(draft.max_selections) >= 1)) {
      this.setState({ error: t('Enter a maximum of 1 or more, or turn the limit off') });
      return;
    }

    this.setState({ busy: true, error: null });
    try {
      const result = await this.productService.saveExtras({
        venue_id: venue._id,
        extras_list_id: activeId || undefined,
        name: draft.name,
        items: draft.items,
        category_ids: draft.category_ids,
        product_ids: draft.product_ids,
        max_selections: draft.limit_on ? Number(draft.max_selections) : null,
        is_required: draft.is_required,
      });
      if (!result || !result.success) throw result || new Error('Could not save');

      const savedId = result.list._id;

      if (apply) {
        const applied = await this.productService.applyExtras(savedId);
        if (!applied || !applied.success) throw applied || new Error('Could not apply');
        Toast.success(`${t('Applied to')} ${applied.updated} ${applied.updated === 1 ? t('product') : t('products')}`);
        if (this.props.onComplete) this.props.onComplete();
      } else {
        Toast.success(t('Saved'));
      }

      await this.load(savedId);
    } catch (e) {
      this.setState({ busy: false, error: this.errorFrom(e, 'Could not save') });
    }
  };

  remove = async () => {
    const { t } = this.props;
    const { activeId, draft } = this.state;
    if (!activeId) return this.newList();

    const ok = await Confirm.open(t('Delete list'), `${t('Delete')} "${draft.name}" ${t('and remove its extras from every product it was applied to?')}`, t('Yes'), t('No'));
    if (!ok) return;

    this.setState({ busy: true, error: null });
    try {
      const result = await this.productService.deleteExtras(activeId);
      if (!result || !result.success) throw result || new Error('Could not delete');
      if (this.props.onComplete) this.props.onComplete();
      Toast.success(t('Deleted'));
      await this.load();
    } catch (e) {
      this.setState({ busy: false, error: this.errorFrom(e, 'Could not delete') });
    }
  };

  render() {
    const { t } = this.props;
    const { show, venue, lists, categories, products, productSearch, activeId, draft, busy, error } = this.state;
    const search = productSearch.trim().toLowerCase();
    const shown = search ? products.filter((p) => p.name.toLowerCase().includes(search)) : products;

    return (
      show && (
        <ModalLayout title={t('Extras')} subtitle={venue ? venue.name : null} width={640}>
          <div className='tab-header' style={{ marginTop: 16, flexWrap: 'wrap' }}>
            {lists.map((list) => (
              <div key={list._id} className={`tab-header-menu ${list._id === activeId ? 'tab-active' : ''}`} onClick={() => !busy && this.select(list)}>
                {list.name} ({list.items.length})
              </div>
            ))}
            <div className={`tab-header-menu ${activeId ? '' : 'tab-active'}`} onClick={() => !busy && this.newList()}>
              {t('+ New list')}
            </div>
          </div>

          <Row style={{ marginTop: 16 }}>
            <Col md={12}>
              <TextField fullWidth label={t('List name')} value={draft.name} onChange={(e) => this.updateDraft({ name: e.target.value })} />
            </Col>
          </Row>

          <Row>
            <Col md={12} style={{ marginTop: 8 }}>
              <Checkbox
                text={t('Selection required')}
                value={true}
                checked={draft.is_required}
                onChange={(checked) => this.updateDraft({ is_required: checked })}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6} style={{ marginTop: 8 }}>
              <Checkbox
                text={t('Limit number of selections')}
                value={true}
                checked={draft.limit_on}
                onChange={(checked) => this.updateDraft({ limit_on: checked, max_selections: checked ? draft.max_selections || '4' : '' })}
              />
            </Col>
            {draft.limit_on && (
              <Col md={6}>
                <TextField
                  fullWidth
                  label={t('Maximum selections')}
                  value={draft.max_selections}
                  onChange={(e) => this.updateDraft({ max_selections: e.target.value.replace(/[^0-9]/g, '') })}
                />
              </Col>
            )}
          </Row>

          <p className='add-venue-subtitle' style={{ marginTop: 16 }}>
            {t('Items')}
          </p>
          {draft.items.map((item, index) => (
            <Row key={index}>
              <Col md={8}>
                <TextField
                  fullWidth
                  label={t('Name')}
                  value={item.name}
                  onChange={(e) => this.setItem(index, { name: e.target.value })}
                  rightBtnLabel='×'
                  rightBtnClick={() => this.removeItem(index)}
                />
              </Col>
              <Col md={4}>
                <TextField fullWidth label={t('Price')} value={item.price} onChange={(e) => this.setItem(index, { price: e.target.value })} />
              </Col>
            </Row>
          ))}
          <DefaultButton text={t('+ ADD ITEM')} onClick={() => this.updateDraft({ items: [...draft.items, { name: '', price: '' }] })} fullWidth />

          <div style={{ marginTop: 16 }} onPaste={this.onPasteList}>
            <TextField fullWidth label={t('Paste a list here, e.g. Olives +2.00, one per line')} value='' onChange={() => {}} />
          </div>

          <p className='add-venue-subtitle' style={{ marginTop: 16 }}>
            {t('Apply to categories')}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 24px' }}>
            {categories.map((category) => (
              <Checkbox
                key={category._id}
                text={category.name}
                value={category._id}
                checked={draft.category_ids.includes(category._id)}
                onChange={(checked) => this.toggleCategory(category._id, checked)}
              />
            ))}
            {categories.length < 1 && <p>{t('This venue has no categories yet.')}</p>}
          </div>

          <p className='add-venue-subtitle' style={{ marginTop: 16 }}>
            {t('Or apply to single products')}
            {draft.product_ids.length > 0 ? ` (${draft.product_ids.length})` : ''}
          </p>
          <TextField
            fullWidth
            label={t('Search products')}
            value={productSearch}
            onChange={(e) => this.setState({ productSearch: e.target.value })}
          />
          <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 8 }}>
            {shown.map((product) => (
              <Checkbox
                key={product._id}
                text={product.name}
                value={product._id}
                checked={draft.product_ids.includes(product._id)}
                onChange={(checked) => this.toggleProduct(product._id, checked)}
              />
            ))}
            {products.length > 0 && shown.length < 1 && <p>{t('No products match that search.')}</p>}
            {products.length < 1 && <p>{t('This venue has no products yet.')}</p>}
          </div>

          {error && (
            <span className='modal-error-text' style={{ marginTop: 14 }}>
              {error}
            </span>
          )}

          <div className='modal-buttons-container'>
            <div className='modal-button'>
              <DefaultButton text={t('SAVE')} onClick={() => this.save(false)} fullWidth />
            </div>
            <div className='modal-button'>
              <LoadingButton text={t('SAVE AND APPLY')} state={busy ? 'LOADING' : 'DONE'} onClick={() => (draft.category_ids.length || draft.product_ids.length) && this.save(true)} fullWidth />
            </div>
          </div>
          <div className='modal-buttons-container' style={{ marginTop: 12 }}>
            {activeId && (
              <div className='modal-button'>
                <DefaultButton text={t('DELETE LIST')} onClick={this.remove} fullWidth />
              </div>
            )}
            <div className={activeId ? 'modal-button' : 'modal-button-full'}>
              <DefaultButton text={t('CLOSE')} onClick={() => this.show(false)} fullWidth />
            </div>
          </div>
        </ModalLayout>
      )
    );
  }
}

export default withTranslation()(ExtrasModal);
